import React from 'react';
import { useHistory } from 'react-router-dom';
import { handleSearchQuery, extractParameters } from './utils';
import { FilterContext } from './FilterContext';
import useFirstRender from 'utils/helpers/useFirstRender';

export function useFilters(spec, initialFilters = {}) {
    const history = useHistory();

    const { context = {}, setContext } = React.useContext(FilterContext);
    const filtersFromUrl = React.useMemo(() => extractParameters(spec, history.location.search), [spec, history.location.search]);
    const filters = (Object.keys(filtersFromUrl).length > 0 && filtersFromUrl) || context.filters || initialFilters;

    if ('aliases' in spec) {
        Object.keys(filters).forEach(key => {
            if (key in spec.aliases) {
                filters[spec.aliases[key]] = filters[key];
                delete filters[key];
            }
        });
    }

    const setFilters = React.useCallback(
        (filters = {}) => {
            setContext(ctx => {
                let search = handleSearchQuery(spec, filters);
                if (ctx.per_page) {
                    search.per_page = ctx.per_page;
                }
                if (ctx.page !== undefined) {
                    search.page = ctx.page;
                }
                if (ctx.search) {
                    search.search = ctx.search;
                }

                history.push({
                    search: Object.entries(search)
                        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
                        .join('&')
                });
                return {
                    ...ctx,
                    filters: filters
                };
            });
        },
        [history, spec, setContext]
    );

    return [filters, setFilters];
}

export function usePageSize(defaultPageSize = 50) {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(FilterContext);
    const queryParams = new URLSearchParams(history.location.search);
    const isFirstRender = useFirstRender();

    const per_page = queryParams.has('per_page') ? Number(queryParams.get('per_page')) : context.per_page !== undefined ? context.per_page : defaultPageSize;

    const setPageSize = React.useCallback(
        _per_page => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);
                queryParams.set('per_page', _per_page);
                queryParams.set('page', '0');
                history.push({
                    search: queryParams.toString()
                });
                return { ...ctx, per_page: _per_page, page: 0 };
            });
        },
        [history, setContext]
    );

    React.useEffect(() => {
        if (isFirstRender && !queryParams.has('per_page')) {
            setPageSize(defaultPageSize);
        }
    }, [isFirstRender]); // eslint-disable-line react-hooks/exhaustive-deps

    return [per_page, setPageSize];
}

export function useCurrentPage(defaultCurrentPage = 0) {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(FilterContext);
    const queryParams = new URLSearchParams(history.location.search);
    const isFirstRender = useFirstRender();

    const page = queryParams.has('page') ? Number(queryParams.get('page')) : context.page !== undefined ? context.page : defaultCurrentPage;

    const setCurrentPage = React.useCallback(
        _page => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);

                queryParams.delete('page');
                history.push({
                    search: `page=${_page}${queryParams.toString() ? `&${queryParams.toString()}` : ''}`
                });
                return { ...ctx, page: _page };
            });
        },
        [history, setContext]
    );

    React.useEffect(() => {
        if (isFirstRender && !queryParams.has('page')) {
            setCurrentPage(defaultCurrentPage);
        }
    }, [isFirstRender]); // eslint-disable-line react-hooks/exhaustive-deps

    return [page, setCurrentPage];
}

export function useSearch() {
    const history = useHistory();
    const { context = {}, setContext } = React.useContext(FilterContext);
    const queryParams = new URLSearchParams(history.location.search);
    const search = context.search || queryParams.get('search') || '';

    const setSearch = React.useCallback(
        searchValue => {
            setContext(ctx => {
                let queryParams = new URLSearchParams(history.location.search);
                if (searchValue) {
                    queryParams.set('search', searchValue);
                } else {
                    queryParams.delete('search');
                }
                queryParams.set('page', '0');
                history.push({
                    search: queryParams.toString()
                });
                return { ...ctx, search: searchValue, page: 0 };
            });
        },
        [history, setContext]
    );

    return [search, setSearch];
}
